import type { Rng } from '../core/rng';
import type { AudioVoices } from './audioVoices';
import type { SpatialSound } from './spatialAudio';

/** Procedural atmosphere: pitches and timbres are estimates, not historical recordings. */
export function soundEffect(
  voices: AudioVoices,
  sound: SpatialSound,
  rng: Rng,
  activity: number
): void {
  const tone = (hz: number, seconds: number, weight = 1, to = hz, delay = 0) => {
    voices.tone({ hz, seconds, weight, to, delay }, sound);
  };
  const grain = (hz: number, seconds: number, weight = 1, delay = 0) => {
    voices.grain({ hz, seconds, weight, delay }, sound);
  };
  switch (sound.emitter.kind) {
    case 'water':
      grain(rng.range(220, 500), rng.range(0.5, 1.4), 0.6 + activity * 0.4);
      grain(950, 0.25, 0.12, 0.2);
      break;
    case 'rigging':
      tone(rng.range(80, 125), 0.7, 0.2, rng.range(140, 190));
      grain(850, 0.13, 0.1, 0.3);
      if (sound.emitter.canvas) grain(1100, 0.8, 0.2);
      break;
    case 'cargo':
      tone(95, 0.25, activity, 45);
      grain(420, 0.2, 0.4 * activity);
      tone(130, 0.22, 0.25 * activity, 60, 0.22);
      break;
    case 'carpentry':
      carpentry(voices, sound, rng, activity);
      break;
    case 'market':
      grain(550, 0.18, activity * 0.3);
      tone(200, 0.18, activity * 0.4, 100);
      break;
    case 'bird':
      birdCall(voices, sound, rng, activity);
      break;
    case 'footstep':
      tone(140, 0.07, 0.4, 65);
      grain(1800, 0.07, 0.32);
      break;
    case 'hooves':
      for (let i = 0; i < 2; i++) {
        tone(220, 0.09, 0.55, 110, i * 0.08);
        grain(2600, 0.06, 0.3, i * 0.08);
      }
      break;
  }
}

function birdCall(voices: AudioVoices, sound: SpatialSound, rng: Rng, activity: number): void {
  const pitch = rng.range(1450, 2100);
  for (let i = 0; i < 2; i++)
    voices.tone(
      { hz: pitch, seconds: 0.3, weight: activity * 0.45, to: pitch * 0.64, delay: i * 0.27 },
      sound
    );
}

function carpentry(voices: AudioVoices, sound: SpatialSound, rng: Rng, activity: number): void {
  if (rng.chance(0.55)) {
    for (let i = 0; i < 3; i++) {
      voices.tone(
        { hz: 170, seconds: 0.13, weight: activity * 0.45, to: 75, delay: i * 0.46 },
        sound
      );
      voices.grain({ hz: 1600, seconds: 0.07, weight: activity * 0.25, delay: i * 0.46 }, sound);
    }
  } else {
    for (let i = 0; i < 4; i++)
      voices.grain(
        { hz: i % 2 ? 1500 : 1100, seconds: 0.28, weight: activity * 0.45, delay: i * 0.4 },
        sound
      );
  }
}

export function bellStrike(voices: AudioVoices, sound: SpatialSound): void {
  const pitch = sound.emitter.id.includes('oude') ? 196 : 227;
  // Deliberately uneven partials. Janse documents the bells but not their 1538 tuning.
  for (const [ratio, weight, seconds] of [
    [0.5, 0.24, 8],
    [1, 1, 7],
    [1.47, 0.24, 5],
    [2.76, 0.28, 4],
    [4.11, 0.12, 2.5],
  ] as const)
    voices.tone({ hz: pitch * ratio, weight, seconds, echo: true }, sound);
  voices.grain({ hz: 1700, seconds: 0.08, weight: 0.18 }, sound);
}
